// monitorClient.js — thin fetchers for the SuperAdmin Ops Monitor (/monitor).
// Self-contained (own file) so the monitor doesn't entangle with the main client.

import { apiFetch } from './apiFetch.js';

// Shared timeline params. `all` (unbounded) wins, then a custom { from, to }
// range (YYYY-MM-DD), then the rolling `days` preset; when none is set the
// server applies its default window.
function rangeParams(range = {}) {
  const p = new URLSearchParams();
  if (range.all) p.set('all', '1');
  else if (range.from && range.to) { p.set('from', range.from); p.set('to', range.to); }
  else if (range.days) p.set('days', String(range.days));
  return p;
}

export const getMonitorSnapshot = (range = {}, excludeMigrated = false) => {
  const p = rangeParams(range);
  if (excludeMigrated) p.set('excludeMigrated', '1');
  const qs = p.toString();
  return apiFetch(`/api/monitor/snapshot${qs ? `?${qs}` : ''}`);
};
export const getMonitorHealth = () => apiFetch('/api/monitor/health');

// Paginated notification feed (server-side page + filters). Only the shown page
// is fetched — the filter dropdowns are driven by `facets` in the response.
export const getMonitorNotifications = ({ page = 1, perPage = 20, range = {}, q = '', rule = '', event = '', status = '' } = {}) => {
  const p = rangeParams(range);
  p.set('page', String(page));
  p.set('perPage', String(perPage));
  if (q) p.set('q', q);
  if (rule) p.set('rule', rule);
  if (event) p.set('event', event);
  if (status) p.set('status', status);
  return apiFetch(`/api/monitor/notifications?${p.toString()}`);
};
export const getMonitorUsers = (range = {}) => {
  const qs = rangeParams(range).toString();
  return apiFetch(`/api/monitor/users${qs ? `?${qs}` : ''}`);
};
export const getMonitorSecrets = () => apiFetch('/api/monitor/secrets');
export const getMonitorClinicAccess = (limit = 150) => apiFetch(`/api/monitor/clinic-access?limit=${limit}`);
export const getMonitorApiMetrics = (range = {}) => {
  const qs = rangeParams(range).toString();
  return apiFetch(`/api/monitor/api-metrics${qs ? `?${qs}` : ''}`);
};
export const getMonitorReport = (kind, range = {}, excludeMigrated = false) => {
  const p = rangeParams(range);
  p.set('kind', kind);
  if (excludeMigrated) p.set('excludeMigrated', '1');
  return apiFetch(`/api/monitor/report?${p.toString()}`);
};

/**
 * Download a report as a multi-sheet .xlsx. `xlsx` is a heavy lib, dynamically
 * imported only when a report is actually requested.
 */
export async function downloadMonitorReport(kind, range, excludeMigrated) {
  const report = await getMonitorReport(kind, range, excludeMigrated);
  const XLSX = await import('xlsx');
  const wb = XLSX.utils.book_new();
  for (const sheet of report.sheets || []) {
    const aoa = [sheet.columns, ...(sheet.rows || [])];
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    // Excel caps sheet names at 31 chars and forbids a few characters.
    const safeName = String(sheet.name || 'Sheet').replace(/[\\/?*[\]:]/g, ' ').slice(0, 31);
    XLSX.utils.book_append_sheet(wb, ws, safeName);
  }
  const stamp = new Date().toISOString().slice(0, 10);
  XLSX.writeFile(wb, `ae-monitor-${kind}-${stamp}.xlsx`);
}
